import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlusIcon, XIcon, ArrowRightIcon, PaperclipIcon, TrashIcon, EyeIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const ESTADOS = {
  borrador: ["Borrador", "text-text-secondary border-border"],
  pendiente: ["Pendiente de autorización", "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10"],
  devuelta: ["Devuelta para corrección", "text-orange-400 border-orange-500/40 bg-orange-500/10"],
  aprobada: ["Aprobada · por despachar", "text-agro-green border-agro-green/40 bg-agro-green/10"],
  en_transito: ["En tránsito", "text-blue-400 border-blue-500/40 bg-blue-500/10"],
  recibida: ["Recibida", "text-agro-green border-agro-green/40 bg-agro-green/10"],
  rechazada: ["Rechazada", "text-red-400 border-red-400/40 bg-red-500/10"],
  anulada: ["Anulada", "text-red-400 border-red-400/40 bg-red-500/10"],
};
const TIPOS_REGISTRO = {
  creada: "Creación", estado: "Cambio de estado", cambio: "Cambios", revision: "Revisión", comentario: "Comentario",
  adjunto: "Adjunto", evidencia_despacho: "Evidencia de despacho", evidencia_recepcion: "Evidencia de recepción",
  incidencia: "Incidencia", despachado: "Despacho", recibido: "Recepción", anulado: "Anulación",
};
const Estado = ({ e }) => (
  <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${ESTADOS[e]?.[1] || "border-border"}`}>{ESTADOS[e]?.[0] || e}</span>
);

/** Solicitudes de traslado de inventario: el RTC solicita; el Delegado o el Administrador General autoriza;
 *  inventario despacha y recibe desde Transferencias. Bitácora de solo agregar con adjuntos (imagen / PDF). */
export default function TransferRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [nuevo, setNuevo] = useState(null);          // {} = nueva; objeto = editar
  const [detalle, setDetalle] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/transfer-requests", { params: filtro ? { estado: filtro } : {} }); setItems(data || []); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filtro]);
  const abrir = async (t) => {
    try { const { data } = await api.get(`/transfer-requests/${t.id}`); setDetalle(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Layout title="Solicitudes de traslado" subtitle="Solicita traslados entre ubicaciones; se autorizan antes de moverse el inventario"
      actions={<button onClick={() => setNuevo({})} data-testid="new-transfer-request"
        className="bg-agro-green text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"><PlusIcon size={14} weight="bold" /> Nueva solicitud</button>}>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex gap-3">
          <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="tr-filtro"
            className="bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="">Todos los estados</option>
            {Object.entries(ESTADOS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="transfer-requests-table">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Número</th><th className="p-3">Ruta</th><th className="p-3">Productos</th>
                <th className="p-3">Solicitante</th><th className="p-3">Fecha</th><th className="p-3">Estado</th><th className="p-3 pr-5" />
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
                : items.length === 0 ? <tr><td colSpan="7" className="p-10 text-center text-text-muted">Sin solicitudes</td></tr>
                : items.map((t) => (
                  <tr key={t.id} className="border-t border-border/40 hover:bg-white/[0.02]">
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{t.numero}</td>
                    <td className="p-3 text-xs">{t.bodega_origen_codigo} <ArrowRightIcon size={10} className="inline" /> {t.bodega_destino_codigo}</td>
                    <td className="p-3 text-xs">{(t.items || []).map((i) => `${i.codigo} × ${i.cantidad}`).join(", ")}</td>
                    <td className="p-3 text-xs">{t.solicitante?.nombre || t.solicitante?.email}</td>
                    <td className="p-3 text-xs text-text-secondary">{fmtDateTime(t.created_at)}</td>
                    <td className="p-3"><Estado e={t.estado} /></td>
                    <td className="p-3 pr-5 text-right">
                      <button aria-label="Ver" onClick={() => abrir(t)} data-testid={`tr-open-${t.numero}`} className="p-1.5 text-text-muted hover:text-agro-green"><EyeIcon size={16} /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {nuevo && <SolicitudForm inicial={nuevo} onClose={() => setNuevo(null)}
        onSaved={(t) => { setNuevo(null); load(); if (t) setDetalle(t); }} />}
      {detalle && <Detalle t={detalle} onClose={() => setDetalle(null)} onChange={(t) => { setDetalle(t); load(); }}
        onEditar={(t) => { setDetalle(null); setNuevo(t); }} />}
    </Layout>
  );
}

function SolicitudForm({ inicial, onClose, onSaved }) {
  const edit = Boolean(inicial.id);
  const [bodegas, setBodegas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [f, setF] = useState({
    bodega_origen_id: inicial.bodega_origen_id || "", bodega_destino_id: inicial.bodega_destino_id || "",
    motivo: inicial.motivo || "", observaciones: inicial.observaciones || "",
    items: (inicial.items || []).map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })),
  });
  const [archivos, setArchivos] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/warehouses").then(({ data }) => setBodegas((data || []).filter((w) => w.activo !== false && w.tipo !== "transito"))).catch(() => {});
    api.get("/products").then(({ data }) => setProductos((data || []).filter((p) => p.activo !== false))).catch(() => {});
  }, []);
  const guardar = async (enviar) => {
    if (!f.bodega_origen_id || !f.bodega_destino_id) return toast.error("Selecciona origen y destino");
    if (!f.motivo.trim()) return toast.error("Indica el motivo del traslado");
    if (!f.items.length || f.items.some((i) => !i.producto_id || !(Number(i.cantidad) > 0))) return toast.error("Revisa productos y cantidades");
    setSaving(true);
    try {
      const body = { ...f, items: f.items.map((i) => ({ producto_id: i.producto_id, cantidad: Number(i.cantidad) })), enviar: false };
      let { data } = edit ? await api.put(`/transfer-requests/${inicial.id}`, body) : await api.post("/transfer-requests", body);
      if (archivos.length) {
        const fd = new FormData();
        fd.append("texto", "Adjuntos de la solicitud"); fd.append("tipo", "adjunto");
        archivos.forEach((a) => fd.append("archivos", a));
        ({ data } = await api.post(`/transfer-requests/${data.id}/comentarios`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
      }
      if (enviar) ({ data } = await api.post(`/transfer-requests/${data.id}/enviar`));
      toast.success(enviar ? `Solicitud ${data.numero} enviada a autorización` : `Solicitud ${data.numero} guardada`);
      onSaved(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const upd = (i, k, v) => setF((s) => ({ ...s, items: s.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const m = useModalForm({ value: { f, a: archivos.map((x) => x.name) }, onClose, onSave: () => guardar(false) });
  const me = useModalForm({ abierto: false, onSave: () => guardar(true), mensaje: "¿Está seguro de que desea enviar la solicitud a autorización?" });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="transfer-request-form">
        <div className="flex justify-between items-center">
          <h3 className="font-heading text-lg">{edit ? `Editar ${inicial.numero}` : "Nueva solicitud de traslado"}</h3>
          <button aria-label="Cerrar" onClick={m.cerrar}><XIcon size={18} /></button>
        </div>
        {edit && inicial.estado === "aprobada" && (
          <div className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-2">
            Está aprobada: si cambias productos, cantidades, origen o destino volverá a revisión.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Sel l="Origen *" v={f.bodega_origen_id} on={(v) => setF({ ...f, bodega_origen_id: v })} opts={bodegas} testid="tr-origen" />
          <Sel l="Destino *" v={f.bodega_destino_id} on={(v) => setF({ ...f, bodega_destino_id: v })} opts={bodegas} testid="tr-destino" />
        </div>
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Productos y cantidades *</span>
            <button type="button" onClick={() => setF({ ...f, items: [...f.items, { producto_id: "", cantidad: 1 }] })} data-testid="tr-add-item"
              className="text-xs text-agro-green">+ Agregar producto</button>
          </div>
          {f.items.map((it, i) => (
            <div key={i} className="flex gap-2 mb-1.5">
              <select value={it.producto_id} onChange={(e) => upd(i, "producto_id", e.target.value)} data-testid={`tr-prod-${i}`}
                className="flex-1 bg-app border border-border rounded px-2 py-1.5 text-sm">
                <option value="">— Producto —</option>
                {productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre || p.sku}</option>)}
              </select>
              <input type="number" min="0" step="any" value={it.cantidad} onChange={(e) => upd(i, "cantidad", e.target.value)} data-testid={`tr-cant-${i}`}
                className="w-24 bg-app border border-border rounded px-2 py-1.5 text-sm text-right" />
              <button aria-label="Eliminar" type="button" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
            </div>
          ))}
        </div>
        <Txt l="Motivo del traslado *" v={f.motivo} on={(v) => setF({ ...f, motivo: v })} testid="tr-motivo" />
        <Txt l="Observaciones" v={f.observaciones} on={(v) => setF({ ...f, observaciones: v })} />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">Adjuntos (imagen o PDF, máx. 8 MB c/u)</label>
          <input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" data-testid="tr-files"
            onChange={(e) => setArchivos(Array.from(e.target.files || []))} className="text-xs" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
          <button disabled={saving || m.saving || me.saving} onClick={m.guardar} data-testid="tr-guardar" className="px-3 py-1.5 text-sm border border-border rounded">Guardar borrador</button>
          {(!edit || ["borrador", "devuelta"].includes(inicial.estado)) && (
            <button disabled={saving || m.saving || me.saving} onClick={me.guardar} data-testid="tr-enviar" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded">Enviar a autorización</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Detalle({ t, onClose, onChange, onEditar }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [obs, setObs] = useState("");
  const [texto, setTexto] = useState("");
  const [tipo, setTipo] = useState("comentario");
  const [archivos, setArchivos] = useState([]);
  const [busy, setBusy] = useState(false);
  const puede = t.puede || {};
  const revisar = async (accion) => {
    if (accion !== "aprobar" && !obs.trim()) return toast.error(accion === "rechazar" ? "Indica el motivo del rechazo" : "Indica qué se debe corregir");
    setBusy(true);
    try { const { data } = await api.post(`/transfer-requests/${t.id}/revisar`, { accion, observaciones: obs }); setObs(""); onChange(data); toast.success("Decisión registrada"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const enviar = async () => {
    try { const { data } = await api.post(`/transfer-requests/${t.id}/enviar`); onChange(data); toast.success("Enviada a autorización"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const registrar = async () => {
    if (!texto.trim() && !archivos.length) return toast.error("Escribe un comentario o adjunta un archivo");
    const fd = new FormData(); fd.append("texto", texto); fd.append("tipo", tipo);
    archivos.forEach((a) => fd.append("archivos", a));
    setBusy(true);
    try {
      const { data } = await api.post(`/transfer-requests/${t.id}/comentarios`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTexto(""); setArchivos([]); onChange(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const verArchivo = async (a) => {
    try {
      const res = await api.get(`/transfer-requests/${t.id}/archivos/${a.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const inventario = Boolean(user?.effective?.is_admin || (user?.effective?.modulos?.inventario || []).includes("modificar"));
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="transfer-request-detail">
        <div className="p-5 border-b border-border flex justify-between items-start sticky top-0 bg-card">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Solicitud de traslado</div>
            <h3 className="font-heading text-xl">{t.numero} <Estado e={t.estado} /></h3>
            <div className="text-xs text-text-secondary mt-1">
              {t.bodega_origen_codigo} · {t.bodega_origen_nombre} <ArrowRightIcon size={10} className="inline" /> {t.bodega_destino_codigo} · {t.bodega_destino_nombre}
            </div>
            <div className="text-[11px] text-text-muted">Solicitó {t.solicitante?.nombre} ({t.solicitante?.email}) · {fmtDateTime(t.created_at)}</div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar}><XIcon size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase text-text-muted border-b border-border text-left"><th className="p-1.5">Producto</th><th className="p-1.5 text-right">Cantidad</th></tr></thead>
            <tbody>{(t.items || []).map((i) => <tr key={i.producto_id} className="border-b border-border/30"><td className="p-1.5">{i.codigo} · {i.nombre}</td><td className="p-1.5 text-right">{i.cantidad}</td></tr>)}</tbody>
          </table>
          <div className="text-xs"><strong>Motivo:</strong> {t.motivo}{t.observaciones && <> · <strong>Observaciones:</strong> {t.observaciones}</>}</div>
          {t.aprobacion && (
            <div className="text-xs bg-app/60 border border-border rounded p-2">
              <strong>{t.aprobacion.accion}</strong> por {t.aprobacion.por} · {fmtDateTime(t.aprobacion.fecha)}{t.aprobacion.observaciones && ` — ${t.aprobacion.observaciones}`}
              {t.traslado_numero && <div className="mt-1">Traslado generado: <span className="font-mono">{t.traslado_numero}</span></div>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {puede.editar && <button onClick={() => onEditar(t)} data-testid="tr-edit" className="px-3 py-1.5 text-xs border border-border rounded">Editar</button>}
            {puede.enviar && <button onClick={enviar} data-testid="tr-send" className="px-3 py-1.5 text-xs bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded">Enviar a autorización</button>}
            {puede.ejecutar && t.traslado_id && <button onClick={() => navigate("/inventario/transferencias")} className="px-3 py-1.5 text-xs border border-agro-green/40 text-agro-green rounded">Despachar / recibir en Transferencias</button>}
          </div>
          {puede.revisar && (
            <div className="border border-agro-yellow/30 bg-agro-yellow/5 rounded p-3 space-y-2" data-testid="tr-review">
              <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Autorización</div>
              <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="tr-review-obs"
                placeholder="Observaciones (obligatorias para devolver o rechazar)" className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
              <div className="flex justify-end gap-2">
                <button disabled={busy} onClick={() => revisar("rechazar")} className="px-3 py-1.5 text-xs border border-red-500/40 text-red-400 rounded">Rechazar</button>
                <button disabled={busy} onClick={() => revisar("devolver")} data-testid="tr-return" className="px-3 py-1.5 text-xs border border-orange-500/40 text-orange-400 rounded">Devolver</button>
                <button disabled={busy} onClick={() => revisar("aprobar")} data-testid="tr-approve" className="px-4 py-1.5 text-xs bg-agro-green text-black font-medium rounded">Aprobar</button>
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Bitácora de seguimiento</div>
            <ol className="space-y-2 border-l border-border pl-3" data-testid="tr-bitacora">
              {(t.bitacora || []).map((e) => (
                <li key={e.id} className="text-xs">
                  <div className="text-text-muted">{fmtDateTime(e.fecha)} · {e.usuario} · <span className="text-text-primary">{TIPOS_REGISTRO[e.tipo] || e.tipo}</span>
                    {e.estado_nuevo && <> · {ESTADOS[e.estado_anterior]?.[0] || e.estado_anterior || "—"} → {ESTADOS[e.estado_nuevo]?.[0] || e.estado_nuevo}</>}</div>
                  {e.texto && <div>{e.texto}</div>}
                  {e.cambios && Object.keys(e.cambios).length > 0 && <div className="text-text-muted">Cambió: {Object.keys(e.cambios).join(", ")}</div>}
                  {(e.adjuntos || []).map((a) => (
                    <button key={a.id} onClick={() => verArchivo(a)} className="mr-2 text-agro-green hover:underline inline-flex items-center gap-1">
                      <PaperclipIcon size={11} /> {a.nombre}
                    </button>
                  ))}
                </li>
              ))}
            </ol>
          </div>
          {!["rechazada", "anulada"].includes(t.estado) && (
            <div className="border-t border-border pt-3 space-y-2" data-testid="tr-comentar">
              <div className="flex gap-2">
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="bg-app border border-border rounded px-2 py-1.5 text-xs">
                  <option value="comentario">Comentario</option>
                  <option value="incidencia">Incidencia</option>
                  {inventario && <option value="evidencia_despacho">Evidencia de despacho</option>}
                  {inventario && <option value="evidencia_recepcion">Evidencia de recepción</option>}
                </select>
                <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Agregar a la bitácora…" data-testid="tr-comentario"
                  className="flex-1 bg-app border border-border rounded px-2 py-1.5 text-sm" />
              </div>
              <div className="flex justify-between items-center">
                <input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" data-testid="tr-comentario-files"
                  onChange={(e) => setArchivos(Array.from(e.target.files || []))} className="text-xs" />
                <button disabled={busy} onClick={registrar} data-testid="tr-comentario-enviar" className="px-3 py-1.5 text-xs bg-agro-green text-black font-medium rounded">Registrar</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Sel = ({ l, v, on, opts, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">{l}</label>
    <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
      <option value="">— Selecciona —</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.codigo} · {o.nombre}</option>)}
    </select>
  </div>
);
const Txt = ({ l, v, on, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">{l}</label>
    <textarea value={v} onChange={(e) => on(e.target.value)} rows={2} data-testid={testid} className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm" />
  </div>
);
